import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { safeFormatISODate } from '../../util/datetime-fns';
import { useLoggedInState } from './hooks/useLoggedInState';

type Props = {
    driverAge: number;
    pickupDate: Date;
    pickupTime: string;
    returnDate: Date;
    returnTime: string;
    pickupLocationId?: string;
    pickupLocationLatitude?: string;
    pickupLocationLongitude?: string;
    pickupLocation?: string;
    returnLocationIsSame: boolean;
    returnLocationId?: string;
    returnLocationLatitude?: string;
    returnLocationLongitude?: string;
    returnLocation?: string;
    unleash?: Record<string, boolean | string> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const CarSearchHiddenInputs = ({
    driverAge,
    pickupDate,
    pickupTime,
    returnDate,
    returnTime,
    pickupLocationId,
    pickupLocationLatitude,
    pickupLocationLongitude,
    pickupLocation,
    returnLocationIsSame,
    returnLocationId,
    returnLocationLatitude,
    returnLocationLongitude,
    returnLocation,
    unleash,
}: Props) => {
    const router = useRouter();
    const { isLoggedIn } = useLoggedInState();

    useEffect(() => {
        if (router.query?.o) {
            window.history.replaceState(null, '', '?bookmarked=true');
        }
    }, [router.query]);

    const chooseClientId = () => {
        // If user is logged in, use this client id so the user is put in a special rates user group at CarTrawler
        // and will then get better prices from time to time, i.e. when CarTrawler is running campaigns
        if (isLoggedIn) return '406905';

        // When the gclid parameter is set, the user is coming from a Google Ad and should have this client id
        if (router.query?.gclid) return '412158';

        if (router.query?.o === 'car_frontpage') return '584700';

        if (unleash?.carRentalSale) {
            return router.query?.o === 'car_banner' ? '588678' : '879089';
        }

        return '301413';
    };

    const renderLocationInputs = (type: 'pickup' | 'return', id?: string, lat?: string, lng?: string, name?: string) => {
        if (id) {
            return <input type="hidden" name={`${type}LocationId`} value={id} />;
        }
        if (lat && lng) {
            return (
                <>
                    <input type="hidden" name={`${type}LocationLat`} value={lat} />
                    <input type="hidden" name={`${type}LocationLng`} value={lng} />
                    {name && <input type="hidden" name={`${type}Name`} value={name} />}
                </>
            );
        }
        return null;
    };

    return (
        <>
            <input type="hidden" name="clientID" value={chooseClientId()} data-testid="clientIdInput" />
            <input type="hidden" name="currency" value="NOK" />
            <input type="hidden" name="residenceID" value="NO" />
            {router.query?.gclid && <input type="hidden" name="gclid" value={router.query.gclid} />}
            {router.query?.gtm_source && <input type="hidden" name="gtm_source" value={router.query.gtm_source} />}
            {router.query?.gtm_medium && <input type="hidden" name="gtm_medium" value={router.query.gtm_medium} />}
            {router.query?.gtm_content && <input type="hidden" name="gtm_content" value={router.query.gtm_content} />}
            {router.query?.gtm_campaign && <input type="hidden" name="gtm_campaign" value={router.query.gtm_campaign} />}
            <input type="hidden" name="age" value={driverAge} />
            <input
                type="hidden"
                name="pickupDateTime"
                value={`${safeFormatISODate(pickupDate)}T${pickupTime}`}
                data-testid="pickupDateTimeInput"
            />
            <input
                type="hidden"
                name="returnDateTime"
                value={`${safeFormatISODate(returnDate)}T${returnTime}`}
                data-testid="returnDateTimeInput"
            />
            {renderLocationInputs('pickup', pickupLocationId, pickupLocationLatitude, pickupLocationLongitude, pickupLocation)}
            {!returnLocationIsSame &&
                renderLocationInputs('return', returnLocationId, returnLocationLatitude, returnLocationLongitude, returnLocation)}
        </>
    );
};
